import { Options } from "highcharts";
import { exportDataToXLSX } from "./xlsx";

const downloadXLSX = (chart: Highcharts.Chart) => {
  const rows = chart.getDataRows();
  exportDataToXLSX(rows, "chart");
};

export const defaultExport = (downloadCSVFn?: () => void, downloadXLSXFn?: () => void) => {
  return {
    exporting: {
      allowHTML: true,
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
            "separator",
            {
              text: "Download XLSX",
              onclick(): void {
                if (downloadXLSXFn !== undefined) return downloadXLSXFn();
                // @ts-ignore
                downloadXLSX(this);
              },
            },
            {
              text: "Download CSV",
              onclick(): void {
                // @ts-ignore
                if (downloadCSVFn === undefined) return this.downloadCSV();
                return downloadCSVFn();
              },
            },
          ],
        },
      },
      chartOptions: {
        chart: {
          marginLeft: 50,
          marginRight: 50,
        },
        legend: {
          itemStyle: {
            fontSize: "0.65rem",
          },
        },
        plotOptions: {
          bar: {
            dataLabels: {
              style: {
                fontSize: "0.5rem",
              },
            },
          },
        },
        title: {
          style: {
            fontSize: "1rem",
          },
        },
        xAxis: {
          labels: {
            style: {
              fontSize: "0.5rem",
            },
            useHTML: true,
          },
          title: {
            margin: 15,
            style: {
              fontSize: "0.75rem",
            },
          },
        },
        yAxis: {
          labels: {
            style: {
              fontSize: "0.7rem",
            },
          },
          title: {
            margin: 15,
            style: {
              fontSize: "0.75rem",
            },
          },
        },
      },
      enabled: true,
    } as Highcharts.ExportingOptions,
  };
};

const defaultExporting = defaultExport();

export const defaultStyle: Options = {
  credits: {
    enabled: false,
  },
  ...defaultExporting,
  legend: {
    enabled: false,
  },
  responsive: {
    rules: [
      {
        chartOptions: {
          legend: {
            enabled: false,
          },
        },
        condition: {
          maxWidth: 500,
        },
      },
    ],
  },
};

export const defaultAxisLabels = (
  align: Highcharts.AlignValue = "center",
  custom: Highcharts.XAxisLabelsOptions = {}
): Highcharts.XAxisOptions => {
  return {
    labels: {
      align,
      reserveSpace: align === "left" ? true : undefined,
      style: {
        fontSize: "1.4rem",
      },
      ...custom,
    },
  };
};

export const defaultSmallAxisLabels = (step: number | undefined = undefined) => {
  return {
    labels: {
      enabled: true,
      step,
      style: {
        fontSize: "1.3rem",
      },
    },
  };
};

export const defaultTooltipSize = {
  style: {
    fontSize: "1.4rem",
  },
};

export const defaultLabel = {
  style: {
    fontSize: "1.4rem",
  },
};

export const defaultLegend = {
  legend: {
    itemStyle: {
      fontSize: "1.4rem",
    },
  },
};

export const defaultPlotOptions = {
  bar: {
    borderRadius: 10,
    pointWidth: 60,
  },
};

export const defaultColumnPlotOptions = {
  column: {
    borderRadius: 5,
    pointPadding: 0,
  },
} as Highcharts.PlotOptions;

export const pieChartLabels = {
  dataLabels: {
    distance: 5,
    enabled: true,
    format: "{point.percentage:.0f}%",
    /* tslint:disable-next-line */
    ...defaultLabel,
  },
};

/* This function is responsible for centering the tooltip on each bar for series bar charts. */
export const centerSeriesBarTooltip = (chart: Highcharts.Chart) => (
  labelWidth: number,
  labelHeight: number,
  point: Highcharts.TooltipPositionerPointObject
) => {
  const { plotX, plotY } = point;
  const { plotHeight, plotTop, plotLeft } = chart;

  const yTotal = plotHeight + plotTop;
  const distanceFromTopBarToTop = plotY + plotTop;
  const barHeight = yTotal - distanceFromTopBarToTop;

  const yCenter = yTotal - barHeight / 2 - labelHeight / 2;
  const xCenter = plotX + plotLeft - labelWidth / 2;

  return {
    x: xCenter,
    y: yCenter,
  };
};

/* This function is responsible for centering the tooltip on Horizontal Bar Chart. */
export const centerHorizontalBarTooltip = (chart: Highcharts.Chart) => (
  labelWidth: number,
  labelHeight: number,
  point: Highcharts.TooltipPositionerPointObject & { h?: number }
) => {
  const endXPoint = point.plotX + chart.plotLeft;

  /* "h" represents the length of the selected bar, it is not found in TooltipPositionerPointObject type */
  const xLength = point.h || 0;

  const startXPoint = endXPoint - xLength;

  const yCenter = point.plotY + chart.plotTop - labelHeight / 2;
  const xCenter = (startXPoint + endXPoint - labelWidth) / 2;

  return {
    x: xCenter,
    y: yCenter,
  };
};

export const handleStepAreaChart = (chart: Highcharts.Chart, defaultStep: number) => {
  // Calculate the number of steps based on the chart width
  const chartWidth = chart.chartWidth;
  const threshold = 280;
  const currentStep = chart.xAxis[0].options.labels?.step;
  const newStep = 40;

  if (chartWidth < threshold && currentStep === defaultStep) {
    return defaultSmallAxisLabels(newStep);
  }

  if (chartWidth > threshold && currentStep !== defaultStep) {
    return defaultSmallAxisLabels(defaultStep);
  }

  return undefined;
};

export const defaultEmptyTitle = {
  title: {
    text: "",
  },
};

export const defaultTooltip: Highcharts.TooltipOptions = {
  outside: true,
  shape: "square",
  ...defaultTooltipSize,
};

export const defaultIndividualUnitsSettings: Highcharts.Options = {
  credits: {
    enabled: false,
  },
  exporting: {
    enabled: false,
  },
  legend: {
    enabled: false,
  },
  ...defaultEmptyTitle,
};
